import * as React from "react";
import {
    Button,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from "@material-ui/core";
import {API} from "aws-amplify";
import {LoadingSpinner} from "../../../../utils/LoadingSpinner";
import {Alert} from "@material-ui/lab";
import {buildDataEntryUrl, buildPharmacistUrl} from "./ccsLinks";

const TASK_ID_NOT_READY = "Not ready";
const TASK_ID_ERROR = "Error loading task ID";
const UNKNOWN_MEDICATION = "Unknown";

const getErrorMessage = (error: any): string =>
    error?.response?.data?.message || error?.message || "Unknown error";

const styles = {
    textField: {width: 400} as React.CSSProperties,
    formGroup: {width: 400} as React.CSSProperties,
    radioGroup: {width: 225} as React.CSSProperties,
    resultBlock: {
        backgroundColor: "#f5f5f5",
        border: "1px solid lightgray",
        borderRadius: "4px",
        marginBottom: "10px",
        padding: "10px",
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
    } as React.CSSProperties,
    linkRow: {margin: "4px 0"} as React.CSSProperties,
    fullWidthGrid: {width: "100%", maxWidth: "100%"} as React.CSSProperties,
};

interface IPrescriptionInfo {
    prescriptionId: string;
    medicationName: string;
}

interface IPrescriptionLink {
    prescriptionId: string;
    medicationName: string;
    pharmacistUrl: string;
    dataEntryUrl: string;
    // taskId once resolved; null while loading; TASK_ID_NOT_READY when CCS has no task yet.
    taskId: string | null;
}

interface IProps {
    data?: any;
}

interface IState {
    id: string;
    idType: "person" | "profile";
    isLoading: boolean;
    errorMessage: string;
    links: IPrescriptionLink[];
}

/**
 * Flow: take a personId or profileId, list the customer's prescriptions, then per
 * prescription render both deep links (keyed on the prescriptionId / pdrId) and the
 * CLIVE task ID fetched from GET /ccs/task-id (shown as "Not ready" when CCS has no
 * verification task for that prescription yet).
 */
class GetCcsTaskIdSection extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.state = {
            id: "",
            idType: "person",
            isLoading: false,
            errorMessage: "",
            links: [],
        };
    }

    public render() {
        return (
            <Grid container direction="column" justify="center" alignItems="center" spacing={3}>
                <Grid item xs>
                    <Alert variant="filled" severity="info">
                        CCS Deep Links — look up a customer's prescriptions and get the CCS task deep
                        links (pharmacist and data entry) for each, plus the CLIVE task ID. Use the
                        links to open the task in CCS and self-assign it.
                    </Alert>
                </Grid>

                <Grid item xs>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Id Type</FormLabel>
                        <FormGroup style={styles.formGroup}>
                            <RadioGroup
                                aria-label="idType"
                                name="idType"
                                value={this.state.idType}
                                onChange={(e) => this.setState({idType: e.target.value as "person" | "profile", links: [], errorMessage: ""})}
                                style={styles.radioGroup}
                            >
                                <FormControlLabel value="person" control={<Radio/>} label="PersonId"/>
                                <FormControlLabel value="profile" control={<Radio/>} label="ProfileId"/>
                            </RadioGroup>
                        </FormGroup>
                    </FormControl>
                </Grid>

                <Grid item xs>
                    <TextField
                        style={styles.textField}
                        label="Id (Required)"
                        variant="outlined"
                        value={this.state.id}
                        onChange={(e) => this.setState({id: e.target.value})}
                    />
                </Grid>

                <Grid item xs>
                    <Button variant="outlined" color="default" onClick={() => this.handleSubmit()}>
                        Get Deep Links
                    </Button>
                </Grid>

                {this.state.isLoading && <LoadingSpinner/>}

                {this.state.errorMessage && (
                    <Alert variant="filled" severity="error">
                        Error: {this.state.errorMessage}
                    </Alert>
                )}

                {this.state.links.length > 0 && (
                    <Grid item xs={12} style={styles.fullWidthGrid}>
                        <h4>CCS Deep Links ({this.state.links.length} prescription(s))</h4>
                        <Typography variant="caption" color="textSecondary" component="p" style={{marginBottom: 8}}>
                            Links require CCS (Clinical Checking Service) access.
                        </Typography>
                        {this.state.links.map((link) => (
                            <div key={link.prescriptionId} style={styles.resultBlock}>
                                <div><strong>Medication:</strong> {link.medicationName}</div>
                                <div><strong>Prescription ID:</strong> {link.prescriptionId}</div>
                                <div><strong>Task ID:</strong> {link.taskId === null ? "Loading..." : link.taskId}</div>
                                <div style={styles.linkRow}>
                                    <a href={link.pharmacistUrl} target="_blank" rel="noopener noreferrer">
                                        Pharmacist task (clinical verification)
                                    </a>
                                </div>
                                <div style={styles.linkRow}>
                                    <a href={link.dataEntryUrl} target="_blank" rel="noopener noreferrer">
                                        Data entry task
                                    </a>
                                </div>
                            </div>
                        ))}
                    </Grid>
                )}
            </Grid>
        );
    }

    private handleSubmit() {
        if (this.state.isLoading) {
            return;
        }
        if (!this.state.id) {
            alert("Please enter an Id");
            return;
        }

        this.setState({isLoading: true, errorMessage: "", links: []});

        const fetchPrescriptions = this.state.idType === "person"
            ? this.fetchByPersonId()
            : this.fetchByProfileId();

        fetchPrescriptions
            .then((prescriptions: IPrescriptionInfo[]) => {
                if (prescriptions.length === 0) {
                    this.setState({errorMessage: "No prescriptions found for the given Id", isLoading: false});
                    return;
                }
                const links: IPrescriptionLink[] = prescriptions.map((prescription) => ({
                    prescriptionId: prescription.prescriptionId,
                    medicationName: prescription.medicationName,
                    pharmacistUrl: buildPharmacistUrl(prescription.prescriptionId),
                    dataEntryUrl: buildDataEntryUrl(prescription.prescriptionId),
                    taskId: null,
                }));
                this.setState({links, isLoading: false});
                links.forEach((link) => this.loadTaskId(link.prescriptionId));
            })
            .catch((error) => {
                this.setState({errorMessage: getErrorMessage(error), isLoading: false});
            });
    }

    private fetchByPersonId(): Promise<IPrescriptionInfo[]> {
        const path = "/order/prescription/get-prescriptions?personId=" + encodeURIComponent(this.state.id);
        return API.get("ApaqPortalAPI", path, {}).then((response: any) => this.extractPrescriptions(response));
    }

    private fetchByProfileId(): Promise<IPrescriptionInfo[]> {
        const config = {body: {profileId: this.state.id, drugIds: []}, headers: {}};
        return API.post("ApaqPortalAPI", "/prescription/profile/search", config)
            .then((response: any) => this.extractPrescriptions(response));
    }

    private extractPrescriptions(response: any): IPrescriptionInfo[] {
        if (!response || !Array.isArray(response.prescriptions)) {
            return [];
        }
        return response.prescriptions
            .map((item: any) => (typeof item === "string" ? this.tryParse(item) : item))
            .filter((rx: any) => rx && typeof rx.prescriptionId === "string" && rx.prescriptionId.length > 0)
            .map((rx: any): IPrescriptionInfo => ({
                prescriptionId: rx.prescriptionId,
                // Medication name field differs by source: the profile search returns it on the
                // drug object (rxnormName/pillPackName), while get-prescriptions (personId) returns
                // it as a top-level description. Check both so neither path falls back to Unknown.
                medicationName: rx.drug?.rxnormName || rx.drug?.pillPackName || rx.description || UNKNOWN_MEDICATION,
            }));
    }

    private tryParse(value: string): any {
        try {
            return JSON.parse(value);
        } catch (e) {
            return null;
        }
    }

    // Fetches the CLIVE task ID for a single prescription. A 4xx (no verification task yet) is an
    // expected "not ready" state, surfaced as TASK_ID_NOT_READY. Any other failure (5xx, network)
    // is a real problem, so it is surfaced distinctly and logged rather than masked as "not ready".
    private loadTaskId(prescriptionId: string) {
        const path = "/ccs/task-id?prescriptionId=" + encodeURIComponent(prescriptionId);
        API.get("ApaqPortalAPI", path, {})
            .then((response: any) => {
                this.updateTaskId(prescriptionId, response?.taskId || TASK_ID_NOT_READY);
            })
            .catch((error) => {
                const status = error?.response?.status;
                if (status && status >= 400 && status < 500) {
                    this.updateTaskId(prescriptionId, TASK_ID_NOT_READY);
                } else {
                    console.error(`Failed to load CCS task ID for prescriptionId ${prescriptionId}`, error);
                    this.updateTaskId(prescriptionId, TASK_ID_ERROR);
                }
            });
    }

    private updateTaskId(prescriptionId: string, taskId: string) {
        this.setState((prev) => ({
            links: prev.links.map((link) =>
                link.prescriptionId === prescriptionId ? {...link, taskId} : link),
        }));
    }
}

export default GetCcsTaskIdSection;
